import {
  Consultation,
  CONSULTATION_STATUS,
  CONSULTATION_STEPS,
  ERROR_CODE,
  PostMessageType,
  WEBSOCKET_ACTION,
  WebsocketMessage,
  WebsocketModal,
  WebSocketState
} from '@abiglobalhealth/go-types'
import { createContext, Dispatch, MutableRefObject, PropsWithChildren, SetStateAction, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isWebsocketConnected } from '@/api/actions'
import { getCurrentUserConsultation } from '@/api/consultations'
import { getEnvUrl } from '@/api/utils'
import { ConfirmationModal } from '@/components/ConfirmationModal'
import { RefreshIcon } from '@/components/icons/RefreshIcon'
import { useConsultation } from '@/hooks/useConsultation'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useJourney } from '@/hooks/useJourney'
import { useLoading } from '@/hooks/useLoading'
import useNavigator from '@/hooks/useNavigator'
import { usePostMessage } from '@/hooks/usePostMessage'
import { useUser } from '@/hooks/useUser'
import { getApiKey } from '@/locale/utils'
import { isConsultationEnded } from '@/utils/consultation'
import { isAbortError } from '@/utils/errors'

const VERSION = 'v4'
const WS_CLOSE_CODE_NO_STATUS = 1005 // No Status Received — connection closed without a close frame

interface WebsocketContextProps {
  websocket: MutableRefObject<WebSocket | null>
  websocketState: WebSocketState
  setWebsocketState: Dispatch<SetStateAction<WebSocketState>>
  connect: () => Promise<WebSocket | void>
  disconnect: () => void
  connectionId?: string
}

const WebsocketContext = createContext({} as WebsocketContextProps)

export const WebsocketProvider = ({ children }: PropsWithChildren) => {
  const websocket = useRef<WebSocket | null>(null)
  const [websocketState, setWebsocketState] = useState<WebSocketState>(WebSocketState.CLOSED)
  const [activeModal, setActiveModal] = useState<WebsocketModal | null>(null)
  const { showLoading, hideLoading } = useLoading()
  const { updateConsultation } = useConsultation()
  const { setNavigator } = useNavigator()
  const { user } = useUser()
  const { sendPostMessage } = usePostMessage()
  const { t } = useTranslation()
  const { initialized } = useJourney()
  const [connectionId, setConnectionId] = useState<string>()
  const [isOngoingVideoCallJoined, setIsOngoingVideoCallJoined] = useState(false)
  const { currentConsultationStep } = useConsultationStep()
  const currentConsultationStepRef = useRef(currentConsultationStep)
  currentConsultationStepRef.current = currentConsultationStep

  const showModal = (modal: WebsocketModal) => setActiveModal(modal)
  const hideModal = () => setActiveModal(null)

  const disconnect = () => {
    console.log('WS - Disconnect?', !!websocket?.current)
    if (!websocket?.current) return

    console.log('\tWS - Disconnecting...')

    setWebsocketState(WebSocketState.CLOSING)

    websocket.current.readyState === WebSocket.OPEN && websocket.current.close()
    websocket.current = null
    hideLoading()
  }

  const handleWebsocketMessage = async (event: MessageEvent) => {
    try {
      const parsedMessage = JSON.parse(event.data) as WebsocketMessage
      console.log('WS [onmessage] - Message received', parsedMessage)

      if (!parsedMessage) return console.warn('WS [onmessage] - No message received')

      const { consultation, navigator, connectionId, action } = parsedMessage
      setConnectionId(connectionId)

      switch (action) {
        case WEBSOCKET_ACTION.CONSULTATION_UPDATED: {
          if (!consultation) return console.warn('WS [onmessage] - No consultation data received')

          const nonSensibleConsultation: Consultation = {
            ...consultation,
            questions: consultation.questions?.map((question) => ({
              ...question,
              question: '**',
              answer: question.answer ? '**' : undefined,
              attachments: question.attachments?.map(() => '**')
            }))
          }
          console.log('WS [onmessage] - Consultation message received', nonSensibleConsultation)

          updateConsultation(consultation)
          await sendPostMessage(PostMessageType.CONSULTATION_MESSAGE, consultation)

          break
        }

        case WEBSOCKET_ACTION.HEALTH_NAVIGATOR_UPDATED:
          console.log('WS [onmessage] - Navigator message received', navigator)
          if (!navigator) return console.warn('WS [onmessage] - No navigator data received')
          setNavigator(navigator)
          await sendPostMessage(PostMessageType.NAVIGATOR_MESSAGE, { ...navigator, conversationId: navigator.healthNavigator.conversationId! })
          break

        case WEBSOCKET_ACTION.PDF_UPDATED:
          console.log('WS [onmessage] - PDF updated message received', parsedMessage, currentConsultationStepRef.current)
          if (currentConsultationStepRef.current === CONSULTATION_STEPS.LANDING)
            return console.log('WS [onmessage] - Landing step, skipping PDF update message')
          console.log('WS [onmessage] - Processing PDF update message', consultation)
          if (consultation) updateConsultation(consultation)
          await sendPostMessage(PostMessageType.CONSULTATION_MESSAGE, consultation)
          break

        default:
          console.warn('WS [onmessage] - Unknown action type received:', action)
      }
    } catch (error) {
      console.error('WS [onmessage] - Error handling message', error)
      if (!isAbortError(error)) throw error
    }
  }

  const connect = async (): Promise<WebSocket | void> => {
    console.log('WS - Connect?', websocket?.current, websocketState)
    const uniqueId = user?.data?.uniqueId
    if (!uniqueId) return

    showLoading()
    const currentConsultation = await getCurrentUserConsultation({ uniqueId })
    console.log('WS - Current consultation on connect:', currentConsultation)

    const shouldRestoreConsultation =
      !currentConsultation ||
      !isConsultationEnded(currentConsultation) ||
      currentConsultation.status === CONSULTATION_STATUS.CLOSED_WITH_REPORT_ACCESS

    if (shouldRestoreConsultation) {
      updateConsultation(currentConsultation)
      console.log('WS - Restorable consultation state found, proceeding with consultation update')
    } else {
      console.log('WS - Ongoing consultation found, but it is ended, skipping consultation update')
    }

    const { isConnected } = await isWebsocketConnected({ uniqueId, connectionId })
    if (isConnected) {
      const alreadyJoined = currentConsultation?.status === CONSULTATION_STATUS.ONGOING_VIDEO_CALL && !!currentConsultation?.video?.patientJoinedAt
      setIsOngoingVideoCallJoined(alreadyJoined)
      hideLoading()
      if (alreadyJoined || !activeModal || activeModal === WebsocketModal.DISCONNECT) return showModal(WebsocketModal.ALREADY_CONNECTED)
    }

    return new Promise((resolve) => {
      if (websocket.current) {
        console.log('\tWS - Already connected', websocket.current)
        return resolve(websocket.current)
      }

      if (websocketState === WebSocketState.CONNECTING) {
        console.log('\tWS - Already connecting...')
        return
      }

      showLoading()

      const apiKey = getApiKey()
      console.log('\tWS - Connecting...', { apiKey, uniqueId })

      if (!apiKey) {
        hideLoading()
        throw new Error(ERROR_CODE.NO_API_KEY)
      }

      websocket.current = new WebSocket(`${getEnvUrl('VITE_SOCKET_URL')}?uniqueId=${uniqueId}&token=${apiKey}&version=${VERSION}`)

      websocket.current.onopen = () => {
        console.log('WS [onopen] - Connected?', websocket.current)
        if (!websocket.current || websocket.current.readyState !== WebSocket.OPEN) return
        console.log('\tWS [onopen] - Connected ✔')

        setWebsocketState(WebSocketState.OPEN)

        hideLoading()
        resolve(websocket.current)
      }

      websocket.current.onclose = async (e) => {
        console.log('WS [onclose] - Disconnected | Code:', e.code, '| Reason:', e.reason || '(none)', '| wasClean:', e.wasClean)

        setWebsocketState(WebSocketState.CLOSED)
        websocket.current = null

        if (!e.code || e.code !== WS_CLOSE_CODE_NO_STATUS) {
          console.warn('WS [onclose] - Unexpected close, showing refresh modal')
          showModal(WebsocketModal.DISCONNECT)
          hideLoading()
        }
      }

      websocket.current.onerror = async (e) => {
        setWebsocketState(WebSocketState.CLOSED)
        console.error('WS [onerror] - Error', e)
      }

      websocket.current.onmessage = handleWebsocketMessage
    })
  }

  const handleModalAction = () => {
    hideModal()
    connect()
  }

  useEffect(() => {
    console.log('WS [mount]', { user, initialized })
    if (!user?.data?.uniqueId || !initialized) return
    connect()

    return () => {
      console.log('WS [unmount]')
      disconnect()
    }
  }, [user?.data?.uniqueId, initialized])

  const value = {
    websocket,
    websocketState,
    setWebsocketState,
    connect,
    disconnect,
    connectionId
  }

  return (
    <WebsocketContext.Provider value={value}>
      {children}
      <ConfirmationModal
        isOpen={activeModal === WebsocketModal.DISCONNECT}
        onSuccess={handleModalAction}
        successLabel={
          <span className="flex items-center justify-center gap-2 text-white">
            <RefreshIcon className="h-5 w-5" />
            {t('button.refresh')}
          </span>
        }
      >
        <p className="text-center">{t('modal.refresh.title')}</p>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={activeModal === WebsocketModal.ALREADY_CONNECTED}
        onSuccess={isOngoingVideoCallJoined ? undefined : handleModalAction}
        successLabel={t('button.continue')}
      >
        <p className="text-center font-bold">{t('modal.already_open.title')}</p>
        <p
          className="text-center"
          dangerouslySetInnerHTML={{
            __html: isOngoingVideoCallJoined ? t('modal.already_open.description.video-call') : t('modal.already_open.description')
          }}
        />
      </ConfirmationModal>
    </WebsocketContext.Provider>
  )
}
